import { createTheme, type PaletteMode, type Theme } from "@mui/material/styles";
import { actThemeOptions, actPalette, actLightBackground } from "@act-group/act-ui-library";

/**
 * ACT brand colors, taken from the corporate identity:
 * dark charcoal surfaces, white type, and the signature pink/magenta
 * checkmark accent ("AdvancedCompliance.Tech").
 */
export const ACT_BRAND = {
  pink: {
    light: "#d987b0",
    main: "#c65d92",
    dark: "#a03e70",
  },
  charcoal: "#3a3a3a",
  charcoalDark: "#2f2f2f",
  grey: "#9e9e9e",
  /** Light-mode page/chrome background, matching the ACT SI Console. */
  lightGrey: "#ececec",
} as const;

const brandPrimary = {
  light: ACT_BRAND.pink.light,
  main: ACT_BRAND.pink.main,
  dark: ACT_BRAND.pink.dark,
  contrastText: "#ffffff",
};

const brandSecondary = {
  light: "#5a5a5a",
  main: ACT_BRAND.charcoal,
  dark: "#2a2a2a",
  contrastText: "#ffffff",
};

/**
 * Builds the ACT brand theme for the requested mode. Extends the shared
 * library theme options (typography, shape, shadows, component overrides)
 * with the brand palette. The library chrome (Topbar/Sidebar/Footer) is
 * styled from `background.paper` / `text.primary`, so it follows the mode
 * automatically.
 */
export function getActBrandTheme(mode: PaletteMode): Theme {
  return createTheme({
    ...actThemeOptions,
    palette: {
      ...actPalette,
      mode,
      primary: brandPrimary,
      secondary: brandSecondary,
      ...(mode === "light"
        ? {
            // Page backdrop matches the ACT SI Console's light background
            // (#ececec); cards/panels stay white so they read as surfaces.
            background: { ...actLightBackground, default: ACT_BRAND.lightGrey },
            // Selected nav/list items use the SI Console's selection grey
            action: { selected: "#d6d6d6" },
          }
        : {
            background: {
              default: ACT_BRAND.charcoalDark,
              paper: ACT_BRAND.charcoal,
            },
            text: {
              primary: "#ffffff",
              secondary: "#b5b5b5",
            },
            divider: "rgba(255, 255, 255, 0.12)",
            // Selected nav/list items use the SI Console's dark selection grey
            action: { selected: "#404040" },
          }),
    },
    components: {
      ...actThemeOptions.components,
      MuiDrawer: {
        styleOverrides: {
          paper: {
            // Slightly larger sidebar menu labels (expanded state only —
            // the collapsed rail's mini-labels use their own styling).
            "& .MuiListItemText-primary": { fontSize: "1.0625rem" },
            // In light mode the SI Console renders its chrome in #ececec
            // while cards stay white, so override the drawer surface.
            ...(mode === "light" ? { backgroundColor: ACT_BRAND.lightGrey } : {}),
          },
        },
      },
      ...(mode === "light"
        ? {
            // The library's Topbar (MuiAppBar) draws from `background.paper`;
            // match the SI Console's #ececec chrome. The Topbar sets its
            // background via `sx`, which outranks theme overrides — hence
            // !important.
            MuiAppBar: {
              styleOverrides: {
                root: { backgroundColor: `${ACT_BRAND.lightGrey} !important` },
              },
            },
          }
        : {}),
    },
  });
}
